import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEntityItems,
  selectEntityLoading,
  selectEntityError,
  fetchStart,
  fetchSuccess,
  fetchFailure,
  createSuccess,
  updateSuccess,
  deleteSuccess,
} from "../../../app/store/slices/dataSlice";
import { getEntityThunks } from "../../../app/store/thunks/entityThunks";
import apiService from "../../services/apiService";
import statusService from "../../services/statusService";

// Map endpoint to entity name
const getEntityName = (endpoint) => {
  if (!endpoint) return null;

  const entityMap = {
    'vaccines': 'vaccines',
    'patients': 'patients',
    'appointments': 'appointments',
    'record': 'record',
    'centers': 'centers',
    'vaccination-centers': 'centers',
    'stock': 'stock',
    'medical-staff': 'medicalStaff',
    'users': 'users',
    'suppliers': 'suppliers',
    'districts': 'districts',
    'talukas': 'talukas',
    'villages': 'villages',
    'vaccine-dispatch': 'vaccineDispatch',
    'vaccine-transfers': 'vaccineDispatch',   
  };

  // Extract entity name from endpoint
  const parts = endpoint.toLowerCase().split('/');
  for (const part of parts) {
    if (entityMap[part]) {
      return entityMap[part];
    }
  }

  // Try to find a match in the endpoint
  for (const [key, value] of Object.entries(entityMap)) {
    if (endpoint.toLowerCase().includes(key)) {
      return value;
    }
  }

  return null;
};

export function useCRUDData(endpoint, options = {}) {
  const dispatch = useDispatch();
  const {
    onDataChange,
    autoFetch = true,
    customHandlers = {},
    useRedux = true, // Enable Redux by default
    statusFilterKey = 'status', // Custom key for status filter query param
    crudEndpoint = null, // Separate endpoint for CRUD operations (create, update, delete)
    customFiltersConfig = [], // Custom filter configurations
    dataUrl = null, // Fallback data URL for local JSON files
  } = options;

  // Use crudEndpoint for CRUD operations if provided, otherwise use main endpoint
  const crudApiEndpoint = crudEndpoint || endpoint;

  // Determine entity name from endpoint
  const entityName = useMemo(() => getEntityName(endpoint), [endpoint]);
  const thunks = useMemo(() => entityName ? getEntityThunks(entityName) : null, [entityName]);

  // Redux state selectors
  const reduxItems = useSelector(entityName ? selectEntityItems(entityName) : () => []);
  const reduxLoading = useSelector(entityName ? selectEntityLoading(entityName) : () => false);
  const reduxError = useSelector(entityName ? selectEntityError(entityName) : () => null);

  // Local state (fallback or for entities not in Redux)
  const [localData, setLocalData] = useState([]);
  const [localLoading, setLocalLoading] = useState(autoFetch);
  const [localError, setLocalError] = useState(null);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [statistics, setStatistics] = useState(null); // Statistics from API response
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customFilterValues, setCustomFilterValues] = useState({}); // Custom filter values
  const searchTermRef = useRef(searchTerm); // Ref to avoid stale closure in handlePageChange
  const statusFilterRef = useRef(statusFilter); // Ref for status filter
  const customFilterValuesRef = useRef(customFilterValues); // Ref for custom filter values
  const [isPageLoading, setIsPageLoading] = useState(false); // Track loading for pagination/search
  const [limit, setLimit] = useState(10); // Records per page (default 10)
  const limitRef = useRef(limit); // Ref for limit
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const sortByRef = useRef('createdAt');
  const sortOrderRef = useRef('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  // Use Redux state if entity is configured and useRedux is enabled
  const shouldUseRedux = useRedux && entityName && thunks;

  // Unified state
  const data = shouldUseRedux ? reduxItems : localData;
  const loading = shouldUseRedux ? reduxLoading : localLoading;
  const error = shouldUseRedux ? reduxError : localError;

  // Normalize items (convert _id to id)
  const normalizeItems = useCallback((items) => {
    return items.map(item => ({
      ...item,
      id: item.id || item._id
    }));
  }, []);

  // Extract pagination from API response
  const extractPagination = useCallback((result) => {
    const paginationData =
      result?.data?.pagination ||
      result?.data?.data?.pagination ||
      result?.pagination ||
      null;

    if (paginationData) {
      return {
        page: paginationData.page || 1,
        limit: paginationData.limit || limitRef.current,
        total: paginationData.total || 0,
        totalPages: paginationData.totalPages || 1
      };
    }
    return null;
  }, []);

  // Extract statistics from API response
  const extractStatistics = useCallback((result) => {
    const statsData =
      result?.data?.statistics ||
      result?.data?.data?.statistics ||
      result?.statistics ||
      null;

    return statsData;
  }, []);

  // Extract items from API response
  const extractItems = useCallback(async (result) => {
    let items = [];

    if (customHandlers?.transformData) {
      items = await customHandlers.transformData(result);
    } else if (result?.data?.data) {
      const responseData = result.data.data;
      if (Array.isArray(responseData)) {
        items = responseData;
      } else if (Array.isArray(responseData.content)) {
        items = responseData.content;
      } else if (Array.isArray(responseData.vaccines)) {
        items = responseData.vaccines;
      } else if (Array.isArray(responseData.suppliers)) {
        items = responseData.suppliers;
      } else if (Array.isArray(responseData.vaccineTransfers)) {
        items = responseData.vaccineTransfers;
      }
    } else if (Array.isArray(result?.data?.suppliers)) {
      items = result.data.suppliers;
    } else if (Array.isArray(result?.data?.vaccines)) {
      items = result.data.vaccines;
    } else if (Array.isArray(result?.data)) {
      items = result.data;
    } else if (Array.isArray(result)) {
      items = result;
    }

    return normalizeItems(Array.isArray(items) ? items : []);
  }, [customHandlers, normalizeItems]);

  // Fetch data from local JSON file as fallback
  const fetchFromDataUrl = useCallback(async () => {
    if (!dataUrl) return null;

    try {
      const response = await fetch(dataUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn(`Failed to load data from ${dataUrl}:`, error);
      return null;
    }
  }, [dataUrl]);

  // Fetch data using Redux or local state - SERVER SIDE ONLY
  const fetchData = useCallback(async (pageNum = page, search = searchTerm, status = statusFilter, customFilters = customFilterValuesRef.current, pageLimit = limitRef.current, sort = sortByRef.current, order = sortOrderRef.current) => {
    setIsPageLoading(true); // Set loading immediately for UI feedback

    // Build custom filters query string
    const buildCustomFiltersQuery = (filters) => {
      let query = '';
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => { query += `&${key}=${encodeURIComponent(v)}`; });
        } else if (value && value !== 'all') {
          query += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      return query;
    };

    if (shouldUseRedux && thunks) {
      // Use Redux thunk
      try {
        dispatch(fetchStart({ entity: entityName }));

        const separator = endpoint.includes("?") ? "&" : "?";
        let endpointWithParams = `${endpoint}${separator}page=${pageNum}&limit=${pageLimit}`;
        if (search) {
          endpointWithParams += `&search=${encodeURIComponent(search)}`;
        }
        if (status && status !== 'all') {
          endpointWithParams += `&${statusFilterKey}=${encodeURIComponent(status.toUpperCase())}`;
        }
        if (sort) {
          endpointWithParams += `&sortBy=${encodeURIComponent(sort)}&order=${encodeURIComponent(order)}`;
        }
        // Add custom filters
        endpointWithParams += buildCustomFiltersQuery(customFilters);

        apiService.clearCache(); // Clear cache to ensure fresh data
        const result = await apiService.getAll(endpointWithParams);
        const items = await extractItems(result);

        // Extract and set pagination
        const paginationInfo = extractPagination(result);
        if (paginationInfo) {
          setPagination(paginationInfo);
        }

        // Extract and set statistics
        const statsInfo = extractStatistics(result);
        if (statsInfo) {
          setStatistics(statsInfo);
        }

        dispatch(fetchSuccess({ entity: entityName, items }));
        onDataChange?.(items);
        setIsPageLoading(false);

        return items;
      } catch (err) {
        console.error("API fetch error:", err);

        // Try fallback to local data
        const fallbackData = await fetchFromDataUrl();
        if (fallbackData) {
          console.log("Using fallback data from local JSON");
          const items = normalizeItems(fallbackData);
          dispatch(fetchSuccess({ entity: entityName, items }));
          onDataChange?.(items);
          setIsPageLoading(false);
          return items;
        }

        dispatch(fetchFailure({ entity: entityName, error: err.message }));
        setIsPageLoading(false);
        throw err;
      }
    } else {
      // Use local state (original behavior)
      setLocalLoading(true);
      try {
        const separator = endpoint.includes("?") ? "&" : "?";
        let endpointWithParams = `${endpoint}${separator}page=${pageNum}&limit=${pageLimit}`;
        if (search) {
          endpointWithParams += `&search=${encodeURIComponent(search)}`;
        }
        if (status && status !== 'all') {
          endpointWithParams += `&${statusFilterKey}=${encodeURIComponent(status.toUpperCase())}`;
        }
        if (sort) {
          endpointWithParams += `&sortBy=${encodeURIComponent(sort)}&order=${encodeURIComponent(order)}`;
        }
        // Add custom filters
        endpointWithParams += buildCustomFiltersQuery(customFilters);

        apiService.clearCache(); // Clear cache to ensure fresh data
        const result = await apiService.getAll(endpointWithParams);
        const items = await extractItems(result);

        // Extract and set pagination
        const paginationInfo = extractPagination(result);
        if (paginationInfo) {
          setPagination(paginationInfo);
        }

        // Extract and set statistics
        const statsInfo = extractStatistics(result);
        if (statsInfo) {
          setStatistics(statsInfo);
        }

        setLocalData(items);
        onDataChange?.(items);
        setLocalError(null);

        return items;
      } catch (err) {
        console.error("API fetch error:", err);

        // Try fallback to local data
        const fallbackData = await fetchFromDataUrl();
        if (fallbackData) {
          console.log("Using fallback data from local JSON");
          const items = normalizeItems(fallbackData);
          setLocalData(items);
          onDataChange?.(items);
          setLocalError(null);
          return items;
        }

        setLocalError(err.message);
        throw err;
      } finally {
        setLocalLoading(false);
        setIsPageLoading(false);
      }
    }
  }, [shouldUseRedux, thunks, entityName, endpoint, limit, extractItems, extractPagination, extractStatistics, onDataChange, dispatch, statusFilter, fetchFromDataUrl, normalizeItems]);

  // Handle page change (uses ref to get latest search term, status, and custom filters)
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchData(newPage, searchTermRef.current, statusFilterRef.current, customFilterValuesRef.current);
  }, [fetchData]);

  // Handle server-side search
  const searchData = useCallback(async (search) => {
    setSearchTerm(search);
    searchTermRef.current = search; // Update ref for handlePageChange
    setPage(1); // Reset to first page on new search
    return fetchData(1, search, statusFilterRef.current, customFilterValuesRef.current);
  }, [fetchData]);

  // Handle server-side status filter
  const filterByStatus = useCallback(async (status) => {
    setStatusFilter(status);
    statusFilterRef.current = status; // Update ref for handlePageChange
    setPage(1); // Reset to first page on filter change
    return fetchData(1, searchTermRef.current, status, customFilterValuesRef.current);
  }, [fetchData]);

  // Handle custom filter change
  const handleCustomFilterChange = useCallback(async (filterKey, value) => {
    const newFilters = { ...customFilterValuesRef.current, [filterKey]: value };
    setCustomFilterValues(newFilters);
    customFilterValuesRef.current = newFilters; // Update ref
    setPage(1); // Reset to first page on filter change
    return fetchData(1, searchTermRef.current, statusFilterRef.current, newFilters);
  }, [fetchData]);

  // Handle limit (records per page) change
  const handleLimitChange = useCallback(async (newLimit) => {
    setLimit(newLimit);
    limitRef.current = newLimit; // Update ref
    setPage(1); // Reset to first page when limit changes
    return fetchData(1, searchTermRef.current, statusFilterRef.current, customFilterValuesRef.current, newLimit);
  }, [fetchData]);

  // Handle server-side sort
  const handleServerSort = useCallback((field) => {
    let newOrder = 'asc';
    if (sortByRef.current === field) {
      newOrder = sortOrderRef.current === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(field);
    setSortOrder(newOrder);
    sortByRef.current = field;
    sortOrderRef.current = newOrder;
    setPage(1);
    return fetchData(1, searchTermRef.current, statusFilterRef.current, customFilterValuesRef.current, limitRef.current, field, newOrder);
  }, [fetchData]);

  // Fetch statuses - use config's statusOptionsLoader if provided, otherwise fall back to statusService
  const fetchStatuses = useCallback(async () => {
    try {
      if (customHandlers?.statusOptionsLoader) {
        const statuses = await customHandlers.statusOptionsLoader();
        setAvailableStatuses(statuses);
      } else {
        const statuses = await statusService.getStatuses();
        setAvailableStatuses(statuses);
      }
    } catch (err) {
      console.error("Status fetch failed", err);
    }
  }, [customHandlers]);

  // Initial fetch on mount - always fetch fresh data
  useEffect(() => {
    if (autoFetch) {
      fetchData();
      fetchStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create item - SERVER SIDE ONLY
  const createItem = useCallback(async (item) => {
    const response = await apiService.create(crudApiEndpoint, item);

    // Extract the created item from response
    const createdItem = response?.data?.data || response?.data || response;
    const normalizedItem = { ...createdItem, id: createdItem.id || createdItem._id };

    if (shouldUseRedux && entityName) {
      dispatch(createSuccess({ entity: entityName, item: normalizedItem }));
    }

    setPage(1);
    await fetchData();

    return response;
  }, [crudApiEndpoint, shouldUseRedux, entityName, dispatch, fetchData]);

  // Update item - SERVER SIDE ONLY
  const updateItem = useCallback(async (id, updates) => {
    const response = await apiService.update(crudApiEndpoint, id, updates);

    // Extract the updated item from response
    const updatedItem = response?.data?.data || response?.data || response;
    const normalizedItem = { ...updatedItem, id: updatedItem.id || updatedItem._id || id };
    if (shouldUseRedux && entityName) {
      dispatch(updateSuccess({ entity: entityName, item: normalizedItem }));
      await fetchData();
      fetchStatuses();
    }

    return response;
  }, [crudApiEndpoint, shouldUseRedux, entityName, dispatch, fetchData]);

  // Delete item - SERVER SIDE ONLY
  const deleteItem = useCallback(async (id) => {
    await apiService.delete(crudApiEndpoint, id);

    if (shouldUseRedux && entityName) {
      dispatch(deleteSuccess({ entity: entityName, id }));
    }

    // Clear cache to force fresh data (refresh is handled by useCRUDOperations)
    apiService.clearCache();
  }, [crudApiEndpoint, shouldUseRedux, entityName, dispatch]);

  // Manual update (used by custom modals) - Updates Redux/local state only
  const updateData = useCallback(
    (newData) => {
      if (shouldUseRedux && entityName) {
        dispatch(fetchSuccess({ entity: entityName, items: newData }));
      } else {
        setLocalData(newData);
      }
      onDataChange?.(newData);
    },
    [shouldUseRedux, entityName, dispatch, onDataChange]
  );

  return {
    // State
    data,
    loading: loading || isPageLoading, // Combine both loading states
    isPageLoading, // Expose separate page loading state
    error,
    availableStatuses,
    statistics, // Statistics from API response (e.g., { total, completed, pending })
    pagination,
    currentPage: page,
    searchTerm,
    statusFilter, // Current status filter value for dropdown
    customFilterValues, // Custom filter values for additional dropdowns

    // Actions
    fetchData,
    refreshData: fetchData,
    createItem,
    updateItem,
    deleteItem,
    setData: updateData,
    handlePageChange,
    setPage: handlePageChange,
    searchData,
    filterByStatus,
    handleCustomFilterChange, // Handle custom filter change
    handleLimitChange, // Handle records per page change
    handleServerSort,
    sortBy,
    sortOrder,

    // Redux dispatch for custom actions
    dispatch,

    // Entity info
    entityName,
    isUsingRedux: shouldUseRedux,
  };
}

export default useCRUDData;
